import { FC, useCallback, useMemo } from "react";
import { IconButton } from "@mui/material";
import PostAddOutlinedIcon from "@mui/icons-material/PostAddOutlined";
import { getExistingTasksFromOpportunities } from "../../../../savings-opportunities/components/toolbar/actions/utils/helpers/helpers";
import { TaskCreateActionItemTooltipContent } from "../../../task-management/components/common/TaskCreateActionItemTooltipContent";
import { useAppDispatch } from "@/store/hooks";
import { taskManagementInitiateEmptyTaskThunk } from "@/store/task-management/thunks/form/taskManagementInitiateEmptyTaskThunk";
import { setTaskManagementTaskTarget } from "@/store/task-management/taskManagementSlice";
import {
  KubernetesAiSavingsOpportunity,
  SavingsOpportunity,
} from "@/services/cloudchipr.api";
import { TooltipWithContent } from "@/components/common/TooltipWithContent";
import { useAppAbility } from "@/services/permissions";
import { getUserPermissionInfoText } from "@/components/pages/utils/helpers/getUserPermissionInfoText";
import { TaskOpportunityTarget } from "@/store/task-management/utils/types/types";
import { isKubernetesAiSavingsOpportunity } from "@/components/pages/ai-kubernetes-recommendations/utils/helpers/isKubernetesAiSavingsOpportunity";

interface OpportunitiesCreateTaskToolbarActionProps {
  opportunities?: SavingsOpportunity[] | KubernetesAiSavingsOpportunity[];
}

export const OpportunitiesCreateTaskToolbarAction: FC<
  OpportunitiesCreateTaskToolbarActionProps
> = ({ opportunities }) => {
  const dispatch = useAppDispatch();
  const { cannot } = useAppAbility();
  const canNotCreateTask = cannot("create", "task");

  const opportunitiesWithTasks = useMemo(
    () => opportunities?.filter((opportunity) => !!opportunity.task),
    [opportunities],
  );
  const atLeastOneTaskExist = !!opportunitiesWithTasks?.length;
  const allHadTask = opportunitiesWithTasks?.length === opportunities?.length;

  const targets = useMemo(
    () =>
      opportunities?.map((opportunity) => ({
        targetId: opportunity.resource_id ?? "",
        task: opportunity.task,
        targetName: opportunity.resource_id ?? "",
      })),
    [opportunities],
  );

  const clickHandler = useCallback(async () => {
    const firstOpportunity =
      opportunities?.length === 1 ? opportunities?.at(0) : undefined;

    await dispatch(
      taskManagementInitiateEmptyTaskThunk(
        firstOpportunity
          ? { description: firstOpportunity.description }
          : undefined,
      ),
    );

    dispatch(
      setTaskManagementTaskTarget({
        type: "opportunity",
        existingTasks: getExistingTasksFromOpportunities(opportunities),
        opportunities:
          opportunities?.map((opportunity) => {
            return {
              ...opportunity,
              state: null,
              cloud_provider: isKubernetesAiSavingsOpportunity(opportunity)
                ? "kubernetes"
                : opportunity.account.provider,
              provider_identifier: opportunity.resource_id,
              current_type: opportunity.resource_type,
              resource_type: opportunity.service,
              price_per_month: opportunity.current_monthly_price,
              live_resource_link_response: null,
              forecasted_end_of_month: opportunity.forecasted_end_of_month,
              listing_price: opportunity.listing_price ?? null,
              billing_cost: opportunity.billing_cost ?? null,
            } as TaskOpportunityTarget;
          }) ?? [],
      }),
    );
  }, [dispatch, opportunities]);

  const disabled = !opportunities?.length;
  const tooltipText = getTooltipText(disabled, canNotCreateTask);

  return (
    <TooltipWithContent
      title={
        atLeastOneTaskExist ? (
          <TaskCreateActionItemTooltipContent
            allTargetsHasTasks={
              opportunitiesWithTasks?.length === opportunities?.length
            }
            targets={targets}
          />
        ) : (
          tooltipText
        )
      }
    >
      <span>
        <IconButton
          size="small"
          onClick={clickHandler}
          disabled={disabled || allHadTask || canNotCreateTask}
        >
          <PostAddOutlinedIcon
            color={
              disabled || allHadTask || canNotCreateTask
                ? "disabled"
                : "primary"
            }
            fontSize="small"
          />
        </IconButton>
      </span>
    </TooltipWithContent>
  );
};

const getTooltipText = (disabled: boolean, canNotCreateTask: boolean) => {
  if (canNotCreateTask) {
    return getUserPermissionInfoText("Create Task");
  }

  return `${disabled ? "Select rows to " : ""}Create Task`;
};
